import './RangeFilter.scss';
import cn from 'classnames';
import { RowData } from '@tanstack/react-table';
import { LegacyColumn } from '@tanstack/react-table/legacy';
import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Appearance, Design, Size } from '@Components/designConstants';
import { FilterDropdown } from './FilterDropdown';

interface RangeFilterProps<TData extends RowData> {
  column: LegacyColumn<TData>;
  label: string;
  displayDecimals: number;
}

/**
 * Range filter for numeric columns
 * - Filters on change (no submit button)
 * - Partial filtering supported (min only, max only, or both)
 * - Bounds respect other active column filters via faceted model
 */
export const RangeFilter = <TData extends RowData>({
  column,
  label,
  displayDecimals
}: RangeFilterProps<TData>) => {
  const facetedMinMax = column.getFacetedMinMaxValues();
  const [facetedMin, facetedMax] = facetedMinMax ?? [0, 0];
  const hasData = facetedMinMax != null;

  // Tolerance handles display rounding (e.g., 879.9 displays as "880")
  const tolerance = 0.5 * 10 ** -displayDecimals;

  const currentFilter = column.getFilterValue() as [number, number] | undefined;
  const [minInput, setMinInput] = useState('');
  const [maxInput, setMaxInput] = useState('');

  const isActuallyFiltering =
    currentFilter && (currentFilter[0] > facetedMin || currentFilter[1] < facetedMax);

  const applyFilter = (min: number, max: number) => {
    if (min <= facetedMin && max >= facetedMax) {
      column.setFilterValue(undefined);
    } else {
      column.setFilterValue([min, max]);
    }
  };

  // Snap to exact bounds when user input is within 0.01 of faceted min/max
  const getAdjustedMin = (parsed: number): number => {
    if (Number.isNaN(parsed)) {
      return -Infinity;
    }
    if (Math.abs(parsed - facetedMin) < 0.01) {
      return facetedMin;
    }
    return parsed - tolerance;
  };

  const getAdjustedMax = (parsed: number): number => {
    if (Number.isNaN(parsed)) {
      return Infinity;
    }
    if (Math.abs(parsed - facetedMax) < 0.01) {
      return facetedMax;
    }
    return parsed + tolerance;
  };

  const handleMinChange = (value: string) => {
    setMinInput(value);
    applyFilter(getAdjustedMin(parseFloat(value)), getAdjustedMax(parseFloat(maxInput)));
  };

  const handleMaxChange = (value: string) => {
    setMaxInput(value);
    applyFilter(getAdjustedMin(parseFloat(minInput)), getAdjustedMax(parseFloat(value)));
  };

  const handleReset = () => {
    setMinInput('');
    setMaxInput('');
    column.setFilterValue(undefined);
  };

  return (
    <FilterDropdown
      label={label}
      active={column.getIsFiltered()}
      disabled={!hasData}
      menuClassName='range-filter-dropdown'
    >
      {!hasData ? (
        <div className='no-data-message'>No numeric data available to filter</div>
      ) : (
        <>
          <div className='range-inputs'>
            <div className='range-input-group'>
              <Form.Label>Min</Form.Label>
              <Form.Control
                type='number'
                value={minInput}
                onChange={e => handleMinChange(e.target.value)}
                step='0.01'
                placeholder={facetedMin.toFixed(2)}
                className={cn({
                  'has-filter': isActuallyFiltering && currentFilter[0] > facetedMin
                })}
              />
            </div>

            <div className='range-input-group'>
              <Form.Label>Max</Form.Label>
              <Form.Control
                type='number'
                value={maxInput}
                onChange={e => handleMaxChange(e.target.value)}
                step='0.01'
                placeholder={facetedMax.toFixed(2)}
                className={cn({
                  'has-filter': isActuallyFiltering && currentFilter[1] < facetedMax
                })}
              />
            </div>
          </div>

          <div className='range-footer'>
            <div className='available-range'>
              Available:{' '}
              {Math.max(minInput ? parseFloat(minInput) : -Infinity, facetedMin).toFixed(2)} -{' '}
              {Math.min(maxInput ? parseFloat(maxInput) : Infinity, facetedMax).toFixed(2)}
            </div>
            <Button
              appearance={Appearance.OUTLINED}
              design={Design.NEUTRAL}
              size={Size.SM}
              onClick={handleReset}
            >
              Reset
            </Button>
          </div>
        </>
      )}
    </FilterDropdown>
  );
};
